/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import React from "react";
import Button from "@mui/material/Button";
import { useAppMandantContext } from "../../../../Basis/Components/AppMandantContext";
import Secure from "../../../../Basis/Utils/Secure";
import sessionStorage from "../../../../Basis/Utils/sessionStorage";
import useExecute from "../../../../Basis/Utils/useExecute";
import StripeKontoClient, { Konto } from "../../../../Management/Mandanten/Api/StripeKontoClient";
import SchliesseSchnelleinstiegSchrittAb from "../../Commands/SchliesseSchnelleinstiegSchrittAb";
import { translationKeys, useTranslation } from "../../Internationalisierung/i18nEinstellungen";
import LadeSchnelleinstieg from "../../Queries/LadeSchnelleinstieg";
import Schnelleinstieg, { Schritt } from "../../Queries/Schnelleinstieg";
import { SchnelleinstiegContextProvider } from "./SchnelleinstiegContext";
import SchnelleinstiegSchritte from "./SchnelleinstiegSchritte";
import SchnelleinstiegStart from "./SchnelleinstiegStart";
import { nurEigentuemer, QueryResult } from "./utils";

export type SchnelleinstiegViewProps = {
    children: React.ReactNode;
}

export default nurEigentuemer(function SchnelleinstiegView(props: SchnelleinstiegViewProps) {
    const { t } = useTranslation();
    const [schritt, setSchritt] = React.useState<Schritt>();
    const [schnelleinstiegResult, setSchnelleinstiegResult] = React.useState<QueryResult<Schnelleinstieg>>({ loading: true });
    const [stripeKontoResult, setStripeKontoResult] = React.useState<QueryResult<Konto | null>>({ loading: true });
    const [weiterDisabled, setWeiterDisabled] = React.useState(false);

    const [ueberspringen, setUeberspringen] = React.useState<number | undefined>(
        sessionStorage.schnelleinstieg.ueberspringen);

    const stripeKontoService = new StripeKontoClient();

    const mandant = useAppMandantContext().mandant;
    Secure.useEffect((secure) => {
        if (!("data" in schnelleinstiegResult)) {
            return;
        }

        async function versucheStripeSchrittAbzuschliessen() {
            setStripeKontoResult({ loading: true });
            const konto = await stripeKontoService.kontoLaden();
            if (konto?.status === "AKTIV") {
                return await schliesseSchrittAb();
            }

            secure(setStripeKontoResult)({
                loading: false,
                data: konto,
            });
        }

        function versucheZahlungsmethodeSchrittAbzuschliessen() {
            if (mandant && (mandant.hatZahlungsmethode || !mandant.kundenkontoAutomatischBelasten)) {
                schliesseSchrittAb()
            }
        }

        const naechsterSchritt = schnelleinstiegResult.data.naechsterSchritt;
        if (naechsterSchritt === Schritt.stripe) {
            versucheStripeSchrittAbzuschliessen();
        } else if (naechsterSchritt === Schritt.zahlungsmethode) {
            versucheZahlungsmethodeSchrittAbzuschliessen();
        }
    }, [schnelleinstiegResult, mandant])

    const query = new LadeSchnelleinstieg(data => setSchnelleinstiegResult({ loading: false, data }));

    const schliesseSchnelleinstiegSchrittAb = useExecute(
        new SchliesseSchnelleinstiegSchrittAb([query.name]));

    async function schliesseSchrittAb() {
        if ("data" in schnelleinstiegResult) {
            const abgeschlossenerSchritt = await schliesseSchnelleinstiegSchrittAb({
                geaendert: schnelleinstiegResult.data.geaendert,
                schnelleinstiegId: schnelleinstiegResult.data.schnelleinstiegId,
            }, { noSnackbar: true });

            if (abgeschlossenerSchritt) {
                setSchritt(abgeschlossenerSchritt.naechsterSchritt);
            }
        }
    }

    if (schnelleinstiegResult.loading) {
        return null;
    }

    if (ueberspringen || schnelleinstiegResult.data.istVollstaendigDurchgefuehrt) {
        return <>{props.children}</>;
    }

    return (
        <SchnelleinstiegContextProvider
            value={{
                istDurchgefuehrt: schritt => {
                    return schnelleinstiegResult.data.naechsterSchritt === undefined || schritt < schnelleinstiegResult.data.naechsterSchritt;
                },
                aktuellerSchritt: schritt,
                naechsterSchritt: schnelleinstiegResult.data.naechsterSchritt,
                start: () => {
                    setSchritt(schnelleinstiegResult.data.naechsterSchritt);
                },
                ueberspringen: () => {
                    const jetzt = Date.now();
                    sessionStorage.schnelleinstieg.ueberspringen = jetzt;
                    setUeberspringen(jetzt);
                },
                WeiterButton: props => (
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={props.disabled || weiterDisabled}
                        onClick={() => {
                            setWeiterDisabled(true);
                            props.onClick().then(schliesseSchrittAb).finally(() => {
                                setWeiterDisabled(false);
                            });
                        }}
                    >
                        {t(translationKeys.schnelleinstiegView.weiter)}
                    </Button>
                ),
                stripe: {
                    service: stripeKontoService,
                    kontoResult: stripeKontoResult,
                }
            }}
        >
            {schritt === undefined ? <SchnelleinstiegStart /> : <SchnelleinstiegSchritte />}
        </SchnelleinstiegContextProvider>
    )
});